/*
 * 模块: SKUDetail (主容器)
 * 职责: 数据流管理、组件组合
 * 依赖: custom hooks, 子组件
 * 限制: 文件<150行
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Empty, Radio, Card } from 'antd';
import {
  SKUDetailHeader,
  SKUInfoSection,
  PeriodManagementSection,
  DataAnalysisTabs,
  SearchPerformanceSection,
} from '../components/SKUDetail';
import { AIConfigPanel } from '../components/SKUDetail/AIConfigPanel';
import { useSKUData } from '../hooks/useSKUData';
import { useBrandNetwork } from '../hooks/useBrandNetwork';
import { useMarketMatrix } from '../hooks/useMarketMatrix';
import { useSKUActions } from '../hooks/useSKUActions';
import { useSearchPerformance } from '../hooks/useSearchPerformance';
import { useTabLazyLoad } from './SKUDetail/hooks';
import { AIConfigProvider } from '../contexts/AIConfigContext';
import { SKUProfitAnalysis } from '../components/SKUDetail/SKUProfitAnalysis';
import './SKUDetail.css';

const isDev = process.env.NODE_ENV === 'development';

/**
 * SKU详情页面容器组件
 * 职责：数据流管理、子组件组合
 */
export const SKUDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [dataSource, setDataSource] = useState<'hot-search' | 'search-performance' | 'finance'>('hot-search');

  // 数据管理 Hooks
  const { loading, sku, periods, keywords, competitors, summary, loadData, reload } = useSKUData(id);

  const {
    networkData,
    brandDetails,
    networkLoading,
    brandDetailsLoading,
    fetchNetworkData,
    generateBrandNetwork,
    fetchBrandDetails,
  } = useBrandNetwork(id);

  const {
    matrixData,
    rankRangeData,
    brandShiftData,
    loading: matrixLoading,
    fetchData: fetchMarketMatrixData,
  } = useMarketMatrix(id);

  const { handleExport, handleExportEnhancedExcel, handleDelete } = useSKUActions(
    id,
    sku,
    periods.length,
    reload
  );

  const {
    periods: spPeriods,
    advantageData,
    marketOverview,
    opportunityData,
    keywordPivotData,
    loading: spLoading,
    fetchAdvantageKeywords,
    fetchMarketOverview,
    fetchOpportunityKeywords,
    fetchKeywordPivot,
  } = useSearchPerformance(Number(id));

  const [spSelectedPeriodId, setSpSelectedPeriodId] = React.useState<number | null>(null);

  // Tab 懒加载
  const { handleTabChange } = useTabLazyLoad({
    networkData,
    matrixData,
    keywordPivotData,
    advantageData,
    marketOverview,
    opportunityData,
    spPeriods,
    fetchNetworkData,
    fetchMarketMatrixData,
    fetchKeywordPivot,
    fetchAdvantageKeywords,
    fetchMarketOverview,
    fetchOpportunityKeywords,
  });

  // 初始化选中的周期ID
  useEffect(() => {
    if (spPeriods.length > 0 && !spSelectedPeriodId) {
      setSpSelectedPeriodId(spPeriods[0].id);
    }
  }, [spPeriods, spSelectedPeriodId]);

  // 初始加载
  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  // 自动加载透视表
  useEffect(() => {
    if (spPeriods.length > 0 && !keywordPivotData) {
      if (isDev) console.log('[SKUDetail] ✅ 触发透视表数据加载');
      fetchKeywordPivot();
    }
  }, [spPeriods.length, keywordPivotData, fetchKeywordPivot]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!sku) {
    return <Empty description="SKU不存在" />;
  }

  return (
    <AIConfigProvider>
      <div className="sku-detail-page">
        <SKUDetailHeader
          skuId={id!}
          skuName={sku.name}
          onReload={reload}
          onDelete={handleDelete}
        />

        <SKUInfoSection sku={sku} periodsCount={periods.length} summary={summary ?? null} />

        {sku.id && <AIConfigPanel skuId={sku.id} skuName={sku.name} />}

        <Card style={{ marginBottom: 16 }}>
          <Radio.Group value={dataSource} onChange={(e) => setDataSource(e.target.value)}>
            <Radio.Button value="hot-search">热门搜索词数据</Radio.Button>
            <Radio.Button value="search-performance">搜索查询绩效数据</Radio.Button>
            <Radio.Button value="finance">财务利润分析 (Profit)</Radio.Button>
          </Radio.Group>
        </Card>

        {dataSource === 'finance' && <SKUProfitAnalysis skuId={Number(id)} />}

        {dataSource === 'hot-search' ? (
          <>
            <PeriodManagementSection periods={periods} skuId={Number(id)} onPeriodUploaded={reload} />

            <DataAnalysisTabs
              periodsCount={periods.length}
              keywords={keywords}
              competitors={competitors}
              networkData={networkData}
              brandDetails={brandDetails}
              networkLoading={networkLoading}
              brandDetailsLoading={brandDetailsLoading}
              matrixData={matrixData}
              rankRangeData={rankRangeData}
              brandShiftData={brandShiftData}
              matrixLoading={matrixLoading}
              onTabChange={handleTabChange}
              onExport={handleExport}
              onExportEnhanced={handleExportEnhancedExcel}
              onGenerateNetwork={generateBrandNetwork}
              onRefreshBrandDetails={fetchBrandDetails}
              spPeriodsCount={spPeriods.length}
              spSelectedPeriodId={spSelectedPeriodId}
              advantageData={advantageData}
              marketOverview={marketOverview}
              opportunityData={opportunityData}
              keywordPivotData={keywordPivotData}
              spLoading={spLoading}
            />
          </>
        ) : (
          <SearchPerformanceSection skuId={Number(id)} />
        )}
      </div>
    </AIConfigProvider>
  );
};
